import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveHelp } from '../helpContent';
import HelpButton from './HelpButton';
import Benachrichtigungsglocke from './Benachrichtigungsglocke';

// Same labels as the navigation, so the heading always matches what was clicked.
// The eyebrow entries get a "PC-Einstellungen ›" prefix — they are reached from a
// different place than the sidebar, and the heading is the only thing on the page
// that says which of the two you are in.
// Translation keys, not finished strings — the heading has to follow a language
// switch like everything else. The eyebrow pages carry a "PC settings ›" prefix
// because they are reached from a different place than the sidebar, and the
// heading is the only thing on the page that says which of the two you are in.
const ROUTE_TITLES: Record<string, { key: string; prefixed?: boolean }> = {
  '/': { key: 'nav.dashboard' },
  '/customers': { key: 'nav.customers' },
  '/orders': { key: 'nav.orders' },
  '/products': { key: 'nav.products' },
  '/applications': { key: 'nav.applications' },
  '/instances': { key: 'nav.instances' },
  '/servers': { key: 'nav.servers' },
  '/deployments': { key: 'nav.deployments' },
  '/releases': { key: 'nav.releases' },
  '/users': { key: 'eyebrow.users', prefixed: true },
  '/integrations': { key: 'eyebrow.integrations', prefixed: true },
  '/api-keys': { key: 'eyebrow.apiKeys', prefixed: true },
  '/embed-keys': { key: 'eyebrow.embedKeys', prefixed: true },
  '/base-stacks': { key: 'eyebrow.baseStacks', prefixed: true },
  '/audit': { key: 'eyebrow.auditLog', prefixed: true },
};

function resolveTitleEntry(pathname: string): { key: string; prefixed?: boolean } | null {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  // Detail pages: /products/abc keeps the section heading.
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length >= 2) {
    const base = `/${parts[0]}`;
    if (ROUTE_TITLES[base]) return ROUTE_TITLES[base];
  }
  return null;
}

interface HeaderProps {
  actions?: React.ReactNode;
}

export default function Header({ actions }: HeaderProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const entry = resolveTitleEntry(pathname);
  const title = entry
    ? entry.prefixed
      ? `${t('eyebrow.pcSettings')} › ${t(entry.key)}`
      : t(entry.key)
    : 'juni PC';
  const help = resolveHelp(pathname);

  return (
    <header className="flex items-center justify-between px-6 py-4 bg-surface border-b border-slate-200 shrink-0">
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
        {help && <HelpButton content={help} />}
      </div>
      <div className="flex items-center gap-2">
        {actions}
        <Benachrichtigungsglocke />
      </div>
    </header>
  );
}
